/* eslint-disable jsx-a11y/media-has-caption */
import ContentCutIcon from "@mui/icons-material/ContentCut";
import { Box, IconButton } from "@mui/material";
import dayjs from "dayjs";
import React from "react";
import { useTranslation } from "react-i18next";
import videojs from "video.js";
import Player from "video.js/dist/types/player";
import "video.js/dist/video-js.css";
import { ClipDialog } from "./ClipDialog";
import { VideoClipProps } from "./VideoClip";
import { defaultTime } from "./VideoTimesList";

type AllowClippingOptions = {
  allowClipping: true;
  startDateTime: Date;
  videoSliderValue: number[];
} & Pick<VideoClipProps, "cameraId">;

type ClippingProps =
  | {
      allowClipping: true;
      videoSliderValue: number[];
    }
  | {
      allowClipping?: false;
      videoSliderValue?: number[];
    };

type RecordedVideoProps = {
  // TODO maybe specify options better....
  startDateTime: Date;
  endTime: Date;
  options?: Record<string, string | undefined | boolean | string[] | unknown[]>;
  onReady?: (player: Player) => void;
} & Pick<VideoClipProps, "cameraId"> &
  ClippingProps;

export const RecordedVideo = ({
  options,
  onReady,
  allowClipping,
  endTime,
  ...rest
}: RecordedVideoProps) => {
  const videoRef = React.useRef<HTMLVideoElement | null>(null);
  const playerRef = React.useRef<Player | null>(null);
  const { t } = useTranslation();
  const [startClipping, setStartClipping] = React.useState<boolean>(false);
  const { startDateTime } = rest;
  React.useEffect(() => {
    // Make sure Video.js player is only initialized once

    if (!playerRef.current) {
      // The Video.js player needs to be _inside_ the component el for React 18 Strict Mode.
      const videoElement = videoRef.current;

      if (!videoElement) return;

      playerRef.current = videojs(videoElement, options, () => {
        videojs.log("player is ready");
      });

      // You could update an existing player in the `else` block here
      // on prop change, for example:
    } else {
      const player = playerRef.current;

      player.autoplay(options?.autoplay as boolean);
      player.src(options?.sources);
    }
  }, [startDateTime, endTime, onReady, options, videoRef]);

  // Dispose the Video.js player when the functional component unmounts
  React.useEffect(() => {
    const player = playerRef.current;

    return () => {
      if (player && !player.isDisposed()) {
        player.dispose();
        videojs.time.resetFormatTime();
        playerRef.current = null;
      }
    };
  }, [playerRef]);

  const handleClipping = React.useCallback(() => {
    // let clip: CameraSaveClipDto = {};

    // const videoPlayer = playerRef.current;
    setStartClipping((prev) => !prev);
    // if (startClipping) {
    //   clip.startTime = videoPlayer?.currentTime();
    // }
  }, []);

  const onClose = React.useCallback(() => {
    setStartClipping(false);
  }, [setStartClipping]);

  React.useEffect(() => {
    videojs.time.setFormatTime(
      (seconds: number, totalVideoLenghtSeconds: number) => {
        const startTime = dayjs(startDateTime).add(seconds, "seconds"); // currentTime of the video
        // console.log("START");
        // console.log(dayjs(startTime).diff(startDateTime, "seconds"));
        // console.log(totalVideoLenghtSeconds);
        const videoPlayedSeconds = dayjs(startTime).diff(
          startDateTime,
          "seconds",
        ); // this is time how long video have been played in seconds

        // this is to find end label
        // TODO fix if hover over end this also triggers there
        if (videoPlayedSeconds === totalVideoLenghtSeconds) {
          return (
            defaultTime
              .add(videoPlayedSeconds, "seconds")
              // .add(-seconds, "seconds")
              .format("HH:mm:ss")
          );
        }

        return startTime.format("HH:mm:ss");
      },
    );
  }, [startDateTime]);

  return (
    <Box>
      <video
        ref={videoRef}
        className="video-js vjs-default-skin"
        controls
        preload="auto"
        autoPlay
      />
      {allowClipping ? (
        <Box>
          <IconButton size="small" onClick={handleClipping}>
            <ContentCutIcon />
            {t("clips:clip")}
          </IconButton>

          {startClipping ? (
            <ClipDialog
              endTime={endTime}
              open={startClipping}
              onClose={onClose}
              {...(rest as unknown as AllowClippingOptions)}
              videoPlayer={playerRef.current}
            />
          ) : null}
        </Box>
      ) : null}
    </Box>
  );
};
